import { t } from 'i18next';

import { AnimationShell, Container, Link, Typography } from '@/components';
import { useBreadcrumbs } from '@/features/header';
import { useRegisterGAPageView } from '@/hooks';

import { useSuscriptions } from '@/features/company/hooks';
import { useGetCompanyQuery } from '@/features/company';
import { useGetCurrentUserQuery } from '@/features/users';
import { skipToken } from '@reduxjs/toolkit/query';
import { cn } from '@/utils/styles';

export function PlanSettingsPage() {
  useBreadcrumbs(() => [{ title: t(`Plans & Billing`) }]);
  useRegisterGAPageView('Plans & Billing');

  const { data: user } = useGetCurrentUserQuery();
  const { data: company } = useGetCompanyQuery(user ? { id: user.company.id } : skipToken);
  const { hasPremiumActive } = useSuscriptions();

  const premium = hasPremiumActive(company?.suscriptions ?? []);

  return (
    <Container className="space-y-12">
      <AnimationShell element="article" type="opacity" duration={0} className="mt-4 h-auto overflow-hidden">
        <div className="p-2 pb-12">
          <Typography as="h3" color="primary" size="title" weight="semibold">
            {t('Plans & Billing')}
          </Typography>
          <Typography as="p" size="body-3" color="secondary" weight="normal" className="mt-1">
            {t(
              'Select the plan that best supports your renewable transactions and upgrade your access to data, insights and dedicated project support.'
            )}
          </Typography>

          <div className="mt-8 flex flex-col items-baseline gap-1 sm:flex-row sm:gap-3">
            <Typography as="h3" size="title" color="primary" weight="normal">
              {t('Current plan: ')}
            </Typography>
            <Typography as="h1" size="heading-1" color="primary" weight="semibold">
              {premium ? t('Business') : t('Free')}
            </Typography>
          </div>

          <div className={cn(!premium && 'flex flex-col gap-6 lg:flex-row')}>
            {!premium && (
              <div className="mt-8 flex justify-between gap-2 rounded-lg border-3 border-green-700 p-4 sm:p-8 lg:w-1/2">
                <div className="w-full">
                  <Typography as="h3" size="body-1" color="primary" weight="semibold">
                    {t('Free Plan')}
                  </Typography>
                  <Typography as="p" size="body-2" color="primary" weight="normal" className="pt-3">
                    <span>{t('0 €/year')}</span>
                  </Typography>
                  <Typography as="p" size="body-2" color="primary" weight="normal" className="pt-2">
                    <span>{t('For professionals exploring renewable energy opportunities.')}</span>
                  </Typography>
                  <ul className="mt-4 list-disc space-y-2 pl-8">
                    <li>
                      <Typography as="p" size="body-2" color="primary" weight="normal">
                        <span>
                          {t('Access all active projects: solar, wind, BESS, hydrogen, biogas, hydropower & DCs')}
                        </span>
                      </Typography>
                    </li>
                    <li>
                      <Typography as="p" size="body-2" color="primary" weight="normal">
                        <span>{t('View detailed project information and attached documents')}</span>
                      </Typography>
                    </li>
                    <li>
                      <Typography as="p" size="body-2" color="primary" weight="normal">
                        <span>{t('Send Expressions of Interest (EOIs)')}</span>
                      </Typography>
                    </li>
                    <li>
                      <Typography as="p" size="body-2" color="primary" weight="normal">
                        <span>{t('Connect directly with verified sellers and developers')}</span>
                      </Typography>
                    </li>
                    <li>
                      <Typography as="p" size="body-2" color="primary" weight="normal">
                        <span>{t('Explore advisors and service providers across Europe')}</span>
                      </Typography>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            <div
              className={cn(
                'mt-8 flex justify-between gap-2 rounded-lg border-3 border-green-700 p-4 sm:p-8',
                premium ? 'w-full lg:w-2/3' : 'lg:w-1/2'
              )}
            >
              <div className="w-full">
                <Typography as="h3" size="body-1" color="primary" weight="semibold">
                  {t('Business Plan')}
                </Typography>
                <Typography as="p" size="body-2" color="primary" weight="normal" className="pt-3">
                  <span>{t('3.000 €/year/company')}</span>
                </Typography>
                <Typography as="p" size="body-2" color="primary" weight="normal" className="pt-2">
                  <span>{t('For investors who want market intelligence and tailored project support.')}</span>
                </Typography>
                <ul className="mt-4 mb-8 list-disc space-y-2 pl-8">
                  <li>
                    <Typography as="p" size="body-2" color="primary" weight="normal">
                      <span>{t('Everything included in the Free plan')}</span>
                    </Typography>
                  </li>
                  <li>
                    <Typography as="p" size="body-2" color="primary" weight="normal">
                      <span>{t('Access to detailed data on past renewable transactions and market activity')}</span>
                    </Typography>
                  </li>
                  <li>
                    <Typography as="p" size="body-2" color="primary" weight="normal">
                      <span>{t('Historical transactions and insights')}</span>
                    </Typography>
                  </li>
                  <li>
                    <Typography as="p" size="body-2" color="primary" weight="normal">
                      <span>{t('Personalized feedback on your investment offers')}</span>
                    </Typography>
                  </li>
                  <li>
                    <Typography as="p" size="body-2" color="primary" weight="normal">
                      <span>{t('Receive complete feedback on your Expressions of Interest.')}</span>
                    </Typography>
                  </li>
                  <li>
                    <Typography as="p" size="body-2" color="primary" weight="normal">
                      <span>{t('Dedicated support from our team to identify projects that match your criteria')}</span>
                    </Typography>
                  </li>
                  <li>
                    <Typography as="p" size="body-2" color="primary" weight="normal">
                      <span>{t('On-demand consulting sessions with nTeaser experts')}</span>
                    </Typography>
                  </li>
                  <li>
                    <Typography as="p" size="body-2" color="primary" weight="normal">
                      <span>{t('Priority access to new features and private market updates')}</span>
                    </Typography>
                  </li>
                </ul>
                {!premium && (
                  <Link
                    to="https://buy.stripe.com/4gwcQEaeE1IT0nufZ2"
                    target="_blank"
                    className="font-heading focus:ring-primary-300 hove:text-decoration-none bg-primary-700 hover:bg-primary-800 rounded-lg px-5 py-2.5 text-center text-sm font-medium text-white hover:no-underline focus:outline-hidden"
                  >
                    {t('Upgrade now')}
                  </Link>
                )}
              </div>
            </div>
          </div>

          {premium && (
            <div className="mt-10">
              <div className="mt-4 flex flex-col gap-4 rounded-lg bg-neutral-50 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-8">
                <div>
                  <Typography as="h5" size="title" color="primary" weight="semibold">
                    {t('Manage plan')}
                  </Typography>
                  <Typography as="p" size="body-2" color="secondary" weight="normal">
                    {t('Update card details / Update billing info / Download invoices')}
                  </Typography>
                </div>
                <Link
                  to="https://billing.stripe.com/p/login/5kAaIz6y09Uq4Du4gg"
                  target="_blank"
                  className="font-heading focus:ring-primary-300 hove:text-decoration-none bg-primary-700 hover:bg-primary-800 shrink-0 rounded-lg px-5 py-2.5 text-center text-sm font-medium text-white hover:no-underline focus:outline-hidden"
                >
                  {t('Manage plan')}
                </Link>
              </div>
            </div>
          )}
        </div>
      </AnimationShell>
    </Container>
  );
}
